import { useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import FormInput from '../../components/atoms/Input/Form-Input'
import Button from '../../components/atoms/Buttons'
import { useNavigate } from 'react-router-dom'
import { createToast } from '../../utils/Toast'
import { CREATE_MANUFACTURER } from '../../apollo/mutations/createManufacture'

const Toast = createToast()

function ManufacturersForm() {
  const idRef = useRef(uuidv4())
  const navigate = useNavigate()

  const methods = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      companyName: '',
      subdomain: '',
      isTreeMaker: null,
    },
  })

  const { handleSubmit } = methods
  const [createManufacturer] = useMutation(CREATE_MANUFACTURER)
  const [loading, setLoading] = useState(false)

  const onSubmit = async formData => {
    setLoading(true)
    try {
      const { data } = await createManufacturer({
        variables: {
          input: {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            brandName: formData.companyName?.toLowerCase(),
            subDomain: formData.subdomain,
            isTreeMaker: formData.isTreeMaker?.value,
            type: "Admin"
          },
        },
        refetchQueries: ['listManufacturers'],
      })

      if (data) {
        Toast.fire({
          icon: 'success',
          title: 'Manufacturer created successfully!',
        })
        navigate('/manufacturers')
      }
    } catch (error) {
      console.error('Mutation error:', error)
      Toast.fire({ icon: 'error', title: error.message || 'An error occurred' })
    } finally {
      setLoading(false)
    }
  }

  const onError = errors => {
    console.log('Form errors:', errors)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onError)} noValidate>
        <div className="px-5 sm:px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <FormInput
              name="email"
              label="Email"
              type="email"
              placeholder="Enter email"
              validations={{
                required: { value: true, message: 'Email is required' },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: 'Invalid email address',
                },
              }}
            />
          </div>

          <div>
            <FormInput
              name="firstName"
              label="First Name"
              placeholder="Enter first name"
              validations={{
                required: { value: true, message: 'First Name is required' },
              }}
            />
          </div>

          <div>
            <FormInput
              name="lastName"
              label="Last Name"
              placeholder="Enter last name"
              validations={{
                required: { value: true, message: 'Last Name is required' },
              }}
            />
          </div>

          <div className="md:col-span-2">
            <FormInput
              name="companyName"
              label="Company Name"
              placeholder="Enter company name"
              validations={{
                required: { value: true, message: 'Company Name is required' },
                minLength: {
                  value: 2,
                  message: 'Company Name must be at least 2 characters',
                },
              }}
            />
          </div>

          <div className="md:col-span-2">
            <FormInput
              name="subdomain"
              label="Subdomain"
              placeholder="e.g. acme"
              validations={{
                required: { value: true, message: 'Subdomain is required' },
                pattern: {
                  value: /^(?!-)[a-z0-9-]{1,63}(?<!-)$/,
                  message:
                    'Use lowercase letters, numbers, and hyphens only; no leading/trailing hyphen',
                },
                validate: {
                  noDots: value =>
                    (value && !/\./.test(value)) || 'Do not include dots; enter only the subdomain part',
                  noProtocol: value =>
                    (value && !/^https?:\/\//i.test(value)) || 'Do not include http(s)://',
                },
              }}
            />
          </div>

          <div className="md:col-span-2">
            <FormInput
              name="isTreeMaker"
              label="Tree Maker"
              type="single-select"
              height={56}
              styles={{
                placeholder: (base) => ({
                  ...base,
                  textAlign: 'left',
                  width: '100%',
                  paddingLeft: '12px',
                  margin: 0,
                }),
                singleValue: (base) => ({
                  ...base,
                  textAlign: 'left',
                  paddingLeft: '12px',
                  margin: 0,
                }),
              }}
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
              validations={{
                required: { value: true, message: 'Tree Maker is required' },
              }}
              placeholder="Select Tree Maker"
            />
          </div>
        </div>

        <div className="px-5 sm:px-8 py-4 border-t border-gray-100 flex flex-wrap justify-center sm:justify-end gap-3">
          <Button
            type="button"
            onClick={() => navigate(-1)}
            className="create-saddle-btn !w-[130px] cursor-pointer !bg-gray-100 !text-gray-800 hover:!bg-gray-200"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            loading={loading}
            type="submit"
            className="create-saddle-btn !w-[150px] cursor-pointer"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </form>
    </FormProvider >
  )
}

export default ManufacturersForm
